import React from 'react';
import { keyBy } from 'lodash';
import { RE2JS } from 're2js';

import { type SecondaryStyleDraftScope } from '@neptune/compare-domain';
import { useSubscribeAllAttributesForWidget } from '@neptune/shared/widgets-business-logic';
import {
  isAttributePatternSource,
  isFloatSeriesSource,
  isYCustomExpressionSource,
  Widget,
} from '@neptune/shared/widgets-domain';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { isTableWidget } from '@neptune/table-widget-domain';
import {
  getVegaLiteFloatPlotDescriptors,
  isVegaLiteWidget,
} from '@neptune/vega-lite-widget-domain';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { isDynamicSectionWidget } from '@neptune/widgets/dynamic-section-domain';

import { isChartWidget, WidgetLayout } from 'domain/widget';
import { isSectionWidget } from 'domain/widget/support/section-widget';

const GLOBAL_SECTION_ID = 'global';

function areSetsEqual(left: Set<string>, right: Set<string>) {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
}

const EMPTY_DRAFTS: readonly SecondaryStyleDraftScope[] = [];

export function useSectionAttributesMapping(
  layouts: WidgetLayout[],
  widgets: Widget[],
  drafts: readonly SecondaryStyleDraftScope[] = EMPTY_DRAFTS,
) {
  const [availableAttributes, setAvailableAttributes] = React.useState<Set<string>>(new Set());
  useSubscribeAllAttributesForWidget(
    React.useCallback((data) => {
      const attributes = new Set<string>();

      for (const items of data) {
        if (!items) {
          continue;
        }

        for (const item of items) {
          for (const attribute of item.attributes) {
            if (attribute.attributeType === 'floatSeries') {
              attributes.add(attribute.attributeName);
            }
          }
        }
      }

      setAvailableAttributes((prev) => {
        if (areSetsEqual(prev, attributes)) {
          return prev;
        }

        return attributes;
      });
    }, []),
  );

  const widgetsMap = React.useMemo(() => keyBy(widgets, 'id'), [widgets]);

  const widgetAttributesRE2CacheRef = React.useRef<Map<string, RE2JS>>(new Map());

  const [sectionIdToAttributesMap, widgetIdToSectionIdMap] = React.useMemo(() => {
    const sectionIdToAttributesMap = new Map<string, Set<string>>();
    const widgetIdToSectionIdMap = new Map<string, string>();
    const patternToAttributesMap = new Map<string, string[]>();

    const insert = (attribute: string, sectionId: string) => {
      const attributes = sectionIdToAttributesMap.get(sectionId);

      if (attributes) {
        attributes.add(attribute);
      } else {
        sectionIdToAttributesMap.set(sectionId, new Set<string>([attribute]));
      }
    };

    const getAttributesForPattern = (pattern: string) => {
      const cached = patternToAttributesMap.get(pattern);

      if (cached) {
        return cached;
      }

      let re2 = widgetAttributesRE2CacheRef.current.get(pattern);

      if (!re2) {
        re2 = RE2JS.compile(pattern);
        widgetAttributesRE2CacheRef.current.set(pattern, re2);
      }

      const matchedAttributes = [];

      for (const attribute of availableAttributes) {
        if (re2.matcher(attribute).find()) {
          matchedAttributes.push(attribute);
        }
      }

      patternToAttributesMap.set(pattern, matchedAttributes);

      return matchedAttributes;
    };

    let currentSectionId = GLOBAL_SECTION_ID;

    for (const { id: widgetId } of layouts) {
      const widget = widgetsMap[widgetId];

      if (!widget) {
        continue;
      }

      if (isDynamicSectionWidget(widget)) {
        currentSectionId = widget.id;

        continue;
      }

      if (isSectionWidget(widget)) {
        currentSectionId = widget.id;

        continue;
      }

      const isVegaLite = isVegaLiteWidget(widget);

      if (!isChartWidget(widget) && !isTableWidget(widget) && !isVegaLite) {
        continue;
      }

      widgetIdToSectionIdMap.set(widgetId, currentSectionId);

      if (isVegaLite) {
        if (widget.options.version === 2) {
          getVegaLiteFloatPlotDescriptors(widget.options).forEach((output) =>
            insert(output.styleKey, currentSectionId),
          );
        }

        continue;
      }

      for (const source of widget.sources) {
        if (isFloatSeriesSource(source)) {
          insert(source.value, currentSectionId);
        }

        if (isYCustomExpressionSource(source)) {
          insert(source.value, currentSectionId);
        }

        if (isAttributePatternSource(source)) {
          for (const attribute of getAttributesForPattern(source.value)) {
            insert(attribute, currentSectionId);
          }
        }
      }
    }

    drafts.forEach((draft) => {
      const sectionId =
        (draft.widgetId ? widgetIdToSectionIdMap.get(draft.widgetId) : undefined) ??
        draft.placement?.sectionKey ??
        ((draft.placement?.at ?? 'bottom') === 'bottom' ? currentSectionId : GLOBAL_SECTION_ID);
      widgetIdToSectionIdMap.set(draft.id, sectionId);
      draft.keys.forEach((key) => insert(key, sectionId));
    });

    return [sectionIdToAttributesMap, widgetIdToSectionIdMap];
  }, [availableAttributes, drafts, layouts, widgetsMap]);

  return [sectionIdToAttributesMap, widgetIdToSectionIdMap] as const;
}
